import { z } from 'zod';
import { InvoiceFormatEnum, InvoiceStatusEnum } from './invoice-blob.js';

/**
 * Extraction rules attached to the LLM system prompt (ADR-0005, REQ4.2-4.5).
 *
 * - `scope=generic` rules are tenant-generic and carry a `customerId`.
 * - `scope=customer` rules are tenant-local overrides for one supplier and
 *   therefore carry both `customerId` and `supplierName`.
 *
 * `body` is markdown that the prompt builder concatenates into the system
 * prompt. `version` is monotonic per-rule and used for ordering and the
 * append-only `rule_versions` history.
 */
export const RuleSchema = z.object({
  id: z.string(),
  customerId: z.string().nullable(),
  supplierName: z.string().nullable(),
  title: z.string().min(1),
  body: z.string().min(1),
  active: z.boolean(),
  version: z.number().int().positive(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});
export type Rule = z.infer<typeof RuleSchema>;

/**
 * `scope` is the discriminator the route layer uses to pick between
 * `global_rules` (tenant-generic) and `customer_rules` (tenant-local
 * supplier/customer-specific). The API surface unifies both tables so the UI
 * never needs to know which physical table backs a row.
 */
export const RuleScopeEnum = z.enum(['generic', 'customer']);
export type RuleScope = z.infer<typeof RuleScopeEnum>;

/**
 * Compact list-row used by the rules-management table (#53). The
 * `lastEditor*` fields are the live-row author for v1 - once the versioning
 * helper from #55 is wired we may switch this to the most recent
 * `rule_versions.editor`, but the wire shape stays the same.
 */
export const RuleListItemSchema = z.object({
  id: z.string(),
  scope: RuleScopeEnum,
  customerId: z.string().nullable(),
  customerName: z.string().nullable(),
  supplierName: z.string().nullable(),
  title: z.string(),
  active: z.boolean(),
  version: z.number().int().positive(),
  lastEditorEmail: z.string().email(),
  lastEditedAt: z.string().datetime(),
});
export type RuleListItem = z.infer<typeof RuleListItemSchema>;

/**
 * Query parameters for `GET /rules`.
 *
 * - `scope=generic&customerId=...` narrows to one tenant's generic rules.
 * - `scope=generic` without `customerId` returns every visible tenant-generic
 *   rule in the caller's tenant scope.
 * - `scope=customer&customerId=...` narrows to one customer.
 * - `scope=customer` without `customerId` returns all customer-specific rules
 *   that are visible in the caller's tenant scope.
 */
export const RuleListQuerySchema = z
  .object({
    scope: RuleScopeEnum,
    customerId: z.string().optional(),
    supplierName: z.string().optional(),
  });
export type RuleListQuery = z.infer<typeof RuleListQuerySchema>;

export const RuleListResponseSchema = z.object({
  items: z.array(RuleListItemSchema),
});
export type RuleListResponse = z.infer<typeof RuleListResponseSchema>;

/**
 * Body shape for `POST /rules`. Both rule flavours are tenant-bound and
 * therefore require a non-null `customerId`. The route still enforces the
 * invariant in addition to the schema check (route also verifies
 * `customerScope` for customer-operator).
 */
export const CreateRuleRequestSchema = z
  .object({
    scope: RuleScopeEnum,
    customerId: z.string().nullable(),
    supplierName: z.string().nullable().default(null),
    title: z.string().min(1).max(200),
    /**
     * Markdown body. 8 KB hard cap (REQ4 + #54): keeps the system prompt
     * predictable in size and protects against accidental mega-paste from
     * the operator UI.
     */
    body: z.string().min(1).max(8192),
    active: z.boolean().default(true),
  })
  .superRefine((value, ctx) => {
    if (value.scope === 'generic' && !value.customerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customerId'],
        message: 'customerId required when scope=generic',
      });
    }
    if (value.scope === 'customer' && !value.customerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customerId'],
        message: 'customerId required when scope=customer',
      });
    }
    if (value.scope === 'customer' && (!value.supplierName || value.supplierName.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['supplierName'],
        message: 'supplierName required when scope=customer',
      });
    }
  });
export type CreateRuleRequest = z.infer<typeof CreateRuleRequestSchema>;

/**
 * Body shape for `PUT /rules/:id`. All fields optional; the route patches
 * only the keys present and ignores `undefined` ones.
 */
export const UpdateRuleRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  body: z.string().min(1).max(8192).optional(),
  active: z.boolean().optional(),
});
export type UpdateRuleRequest = z.infer<typeof UpdateRuleRequestSchema>;

/**
 * Single-rule response shape used by create/update endpoints. Mirrors the
 * list-row but drops the editor join (clients can refetch the list if they
 * need it).
 */
export const RuleResponseSchema = z.object({
  id: z.string(),
  scope: RuleScopeEnum,
  customerId: z.string().nullable(),
  supplierName: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  active: z.boolean(),
  version: z.number().int().positive(),
  updatedAt: z.string().datetime(),
});
export type RuleResponse = z.infer<typeof RuleResponseSchema>;

/**
 * Append-only history row. Each save bumps `version` on `rules` and writes a
 * `rule_versions` row carrying the previous body for audit/diff.
 */
export const RuleVersionSchema = z.object({
  id: z.string(),
  ruleId: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  body: z.string(),
  active: z.boolean(),
  changedBy: z.string(),
  changedAt: z.string().datetime(),
});
export type RuleVersion = z.infer<typeof RuleVersionSchema>;

/**
 * Wire-shape of a single entry in the version-history list (#55). Carries the
 * editor's e-mail so the UI can render `Bearbeitet von $email` without a
 * follow-up `/users/$id` lookup.
 */
export const RuleVersionListItemSchema = z.object({
  id: z.string(),
  ruleType: z.enum(['global', 'customer']),
  ruleId: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  body: z.string(),
  active: z.boolean(),
  editorEmail: z.string().email(),
  editedAt: z.string().datetime(),
});
export type RuleVersionListItem = z.infer<typeof RuleVersionListItemSchema>;

/**
 * Response shape for `GET /rules/:id/versions`. `current` is the live row
 * (so the UI can render a "v$N (current)" pseudo-entry above the history),
 * `history` are the snapshots in `rule_versions` sorted by `version desc`.
 */
export const RuleVersionListResponseSchema = z.object({
  current: RuleVersionListItemSchema,
  history: z.array(RuleVersionListItemSchema),
});
export type RuleVersionListResponse = z.infer<typeof RuleVersionListResponseSchema>;

/**
 * Body shape for `POST /rules/:id/rollback`. The route looks up `toVersion`
 * in the history; missing -> 404 with `code='version_not_found'`, equal to
 * the current version -> 409 with `code='rollback.invalid'`.
 */
export const RollbackRuleRequestSchema = z.object({
  toVersion: z.number().int().positive(),
});
export type RollbackRuleRequest = z.infer<typeof RollbackRuleRequestSchema>;

export const RollbackRuleResponseSchema = z.object({
  ruleId: z.string(),
  newVersion: z.number().int().positive(),
  restoredFromVersion: z.number().int().positive(),
});
export type RollbackRuleResponse = z.infer<typeof RollbackRuleResponseSchema>;

/**
 * Test-run input (#56). The multipart route sends the rule draft as a JSON
 * string field plus a file upload; the shared schema only validates the
 * draft payload itself so UI and API can reuse the same rules.
 *
 * `ruleDraft` is the same shape we accept on `POST /rules` so the UI can
 * pass through whatever the user has typed in the edit form without
 * re-mapping. The schema deliberately keeps `id` optional - a draft does
 * not have to exist in the DB.
 */
export const RuleDraftSchema = z
  .object({
    scope: RuleScopeEnum,
    customerId: z.string().nullable(),
    supplierName: z.string().nullable().default(null),
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(8192),
  })
  .superRefine((value, ctx) => {
    if (value.scope === 'generic' && !value.customerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customerId'],
        message: 'customerId required when scope=generic',
      });
    }
    if (value.scope === 'customer' && !value.customerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customerId'],
        message: 'customerId required when scope=customer',
      });
    }
    if (value.scope === 'customer' && (!value.supplierName || value.supplierName.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['supplierName'],
        message: 'supplierName required when scope=customer',
      });
    }
  });
export type RuleDraft = z.infer<typeof RuleDraftSchema>;

export const TestRunRuleMultipartSchema = z.object({
  ruleDraft: z.string().min(1),
});
export type TestRunRuleMultipart = z.infer<typeof TestRunRuleMultipartSchema>;

/**
 * Parsed request payload after the route has unpacked the multipart fields.
 * Kept for test helpers and potential future non-browser callers.
 */
export const TestRunRuleRequestSchema = z.object({
  ruleDraft: RuleDraftSchema,
});
export type TestRunRuleRequest = z.infer<typeof TestRunRuleRequestSchema>;

/**
 * One plausibility issue surfaced by the test-run pipeline. Mirrors the
 * `PlausibilityIssue` shape from `pipeline-core/plausibility/types.ts` but
 * lives in `shared` because the wire shape is consumed by the UI directly.
 */
export const TestRunIssueSchema = z.object({
  code: z.string(),
  level: z.enum(['error', 'warning']),
  field: z.string(),
  message: z.string(),
});
export type TestRunIssue = z.infer<typeof TestRunIssueSchema>;

/**
 * Wire-shape of `POST /rules/test-run`. The route never persists; the
 * response is a snapshot of "what would happen". `valid` mirrors the
 * plausibility result. `blob` carries the candidate `InvoiceBlob` as a
 * structurally-typed `unknown` so the schema does not have to import the
 * full `InvoiceBlobSchema` (avoids a circular dep between rule.ts and
 * invoice-blob.ts in older bundlers; the UI revalidates with the
 * `InvoiceBlobSchema` separately).
 */
export const TestRunRuleResponseSchema = z.object({
  status: InvoiceStatusEnum.extract(['success', 'manual_review']),
  format: InvoiceFormatEnum.nullable(),
  blob: z.unknown(),
  valid: z.boolean(),
  issues: z.array(TestRunIssueSchema),
});
export type TestRunRuleResponse = z.infer<typeof TestRunRuleResponseSchema>;
